import { Link } from "react-router-dom";
import { ArrowDownRight, ArrowRight, Bookmark, Sparkles, XCircle } from "lucide-react";
import { Panel } from "@/components/common/Primitives";
import { LEGEND_ORDER, MARKET_STATUS } from "@/lib/marketStatus";
import { num } from "@/lib/format";

const time = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

export const StatusLegend = () => (
  <Panel title="Status uitleg" testid="status-legend">
    <div className="divide-y divide-slate-100">
      {LEGEND_ORDER.map((key) => {
        const meta = MARKET_STATUS[key];
        const Icon = meta.icon;
        return (
          <div key={key} className="flex items-start gap-2 px-3 py-2">
            <Icon className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${meta.iconColor}`} />
            <div>
              <div className="text-[12px] font-semibold text-slate-800">{meta.label}</div>
              <div className="text-[10.5px] text-slate-500">{meta.hint}</div>
            </div>
          </div>
        );
      })}
    </div>
  </Panel>
);

export const QuickActions = ({ kpis, savedCount, onStatus }) => {
  const actions = [
    { key: "NIEUW", label: "Alle nieuwe bekijken", count: kpis.new_count, icon: Sparkles, color: "text-emerald-600" },
    { key: "PRIJS_VERLAAGD", label: "Prijsverlagingen bekijken", count: kpis.price_drop_count, icon: ArrowDownRight, color: "text-amber-600" },
    { key: "VERDWENEN", label: "Verdwenen voertuigen", count: kpis.disappeared_count, icon: XCircle, color: "text-red-500" },
  ];
  return (
    <Panel title="Snelle acties" testid="quick-actions">
      <div className="divide-y divide-slate-100">
        {actions.map(({ key, label, count, icon: Icon, color }) => (
          <button
            key={key}
            type="button"
            onClick={() => onStatus(key)}
            data-testid={`quick-action-${key.toLowerCase()}`}
            className="flex w-full items-center gap-2 px-3 py-2 text-left transition-colors duration-150 hover:bg-slate-50"
          >
            <Icon className={`h-3.5 w-3.5 shrink-0 ${color}`} />
            <span className="text-[12px] text-slate-700">{label}</span>
            <span className="mono ml-auto text-[11.5px] font-semibold text-slate-500">
              {num(count)}
            </span>
          </button>
        ))}
        <Link
          to="/watchlist"
          data-testid="quick-action-watchlist"
          className="flex w-full items-center gap-2 px-3 py-2 transition-colors duration-150 hover:bg-slate-50"
        >
          <Bookmark className="h-3.5 w-3.5 shrink-0 text-blue-600" />
          <span className="text-[12px] text-slate-700">Watchlist bekijken</span>
          <span className="mono ml-auto text-[11.5px] font-semibold text-slate-500">
            {num(savedCount)}
          </span>
        </Link>
      </div>
    </Panel>
  );
};

export const RecentScans = ({ scans }) => (
  <Panel title="Laatste scans" testid="recent-scans">
    {scans.length === 0 ? (
      <div className="px-3 py-3 text-[11.5px] text-slate-500">Nog geen scans uitgevoerd.</div>
    ) : (
      <div className="divide-y divide-slate-100">
        {scans.map((scan) => (
          <div key={scan.id} className="px-3 py-2" data-testid={`recent-scan-${scan.id}`}>
            <div className="flex items-start justify-between gap-2">
              <span className="truncate text-[12px] font-semibold text-slate-800">
                {scan.profile_name || "Scan"}
              </span>
              <span className="mono shrink-0 text-[11px] text-slate-500">
                {time(scan.created_at)}
              </span>
            </div>
            <div className="mt-0.5 text-[10.5px] text-slate-500">
              {scan.status === "COMPLETED"
                ? `${num(scan.vehicles_found ?? 0)} gevonden · ${num(scan.new_count ?? 0)} nieuw`
                : scan.stage_message || scan.status}
            </div>
          </div>
        ))}
      </div>
    )}
    <Link
      to="/scanhistorie"
      data-testid="all-scan-history"
      className="flex items-center gap-1.5 border-t border-slate-100 px-3 py-2 text-[12px] font-semibold text-blue-700 hover:underline"
    >
      Alle scanhistorie <ArrowRight className="h-3.5 w-3.5" />
    </Link>
  </Panel>
);
